// Форма создания тикета — главный сценарий «Связаться с разрабом».
//
// Поля:
//  - subject (auto-categorize + FAQ-подсказка)
//  - category (6 категорий, idea редиректит на /ideas)
//  - priority (3 чипа, critical только для account/service_down)
//  - text
//  - вложения (до 3 файлов)
//  - guest_email (если не залогинен)
//
// После успешного создания вызывается onCreated(ticketId).
import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { autoCategorize, createTicket } from "../../api/tickets";
import { FlagIcon } from "../icons/FlagIcon";
import { PaperAirplaneIcon } from "../icons/PaperAirplaneIcon";
import TicketCategorySelect from "./TicketCategorySelect";
import TicketPrioritySelect from "./TicketPrioritySelect";
import TicketAttachmentUpload from "./TicketAttachmentUpload";
import FaqSearchHint from "./FaqSearchHint";
import GuestEmailField from "./GuestEmailField";

const CRITICAL_ALLOWED = new Set(["account", "service_down"]);

export default function TicketCreateForm({ onCreated, onPickFaqArticle }) {
  const { isAuthenticated } = useAuth();

  const [subject, setSubject] = useState("");
  const [category, setCategory] = useState("other");
  // Юзер вручную трогал категорию? Если нет — авто-категоризация её
  // может менять. Если да — не перезатираем.
  const [categoryTouched, setCategoryTouched] = useState(false);
  const [priority, setPriority] = useState("normal");
  const [text, setText] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [guestEmail, setGuestEmail] = useState("");

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  // Авто-категоризация по subject (debounce 500мс)
  const acRef = useRef(null);
  useEffect(() => {
    if (categoryTouched) return;
    if ((subject || "").trim().length < 4) return;
    if (acRef.current) clearTimeout(acRef.current);
    acRef.current = setTimeout(async () => {
      try {
        const r = await autoCategorize(subject);
        if (r && r.category && r.category !== "other") {
          setCategory(r.category);
          // priority может стать невалидным для новой категории
          if (priority === "critical" && !CRITICAL_ALLOWED.has(r.category)) {
            setPriority("normal");
          }
        }
      } catch {/* молча */}
    }, 500);
    return () => clearTimeout(acRef.current);
  }, [subject, categoryTouched, priority]);

  const handleCategory = (next) => {
    setCategoryTouched(true);
    setCategory(next);
    if (priority === "critical" && !CRITICAL_ALLOWED.has(next)) {
      setPriority("normal");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);

    if (!subject.trim()) { setErr("Опиши коротко тему."); return; }
    if (!text.trim())    { setErr("Напиши подробности проблемы."); return; }
    if (!isAuthenticated && !guestEmail.trim()) {
      setErr("Укажи email — на него придёт ответ.");
      return;
    }
    // idea — редирект на /ideas, перехватываем тут
    if (category === "idea") {
      window.location.href = "/ideas";
      return;
    }

    setBusy(true);
    try {
      const payload = {
        subject: subject.trim(),
        category,
        priority,
        text: text.trim(),
        attachments: attachments.map((a) => a.file_id),
      };
      if (!isAuthenticated) {
        payload.guest_email = guestEmail.trim();
        // captcha_token подключится в Этапе 6
      }
      const res = await createTicket(payload, { authed: isAuthenticated });
      onCreated && onCreated(res.id);
    } catch (e) {
      setErr(e.message || "Не удалось создать обращение.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="sup-form" onSubmit={submit}>
      {/* subject + FAQ-подсказка */}
      <div>
        <label className="sup-form__label">Кратко в чём вопрос</label>
        <input
          className="sup-form__input"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="Например: меня забанили, как восстановить пароль"
          maxLength={200}
        />
        <FaqSearchHint
          query={subject}
          onPickArticle={(a) => onPickFaqArticle && onPickFaqArticle(a)}
        />
      </div>

      {/* категория */}
      <div>
        <label className="sup-form__label">Категория</label>
        <TicketCategorySelect value={category} onChange={handleCategory} />
        {category === "content_complaint" && (
          <div className="sup-msg-info" style={{ marginTop: 8,
              display: "flex", alignItems: "center", gap: 8 }}>
            <FlagIcon size={16} color="#475569" />
            <span>
              Жалобы на конкретную игру удобнее отправлять кнопкой «Пожаловаться»
              прямо в карточке игры. Если ситуация общая — пиши тут.
            </span>
          </div>
        )}
        {category === "idea" && (
          <div className="sup-msg-info" style={{ marginTop: 8 }}>
            Идеи лучше предлагать в публичном роадмапе — другие юзеры могут
            проголосовать за твою. После отправки переадресую на /ideas.
          </div>
        )}
      </div>

      {/* приоритет */}
      <div>
        <label className="sup-form__label">Срочность</label>
        <TicketPrioritySelect value={priority} onChange={setPriority}
                              category={category} />
        <div className="sup-form__hint">
          Критический — только для подписки, аккаунта и сервиса.
        </div>
      </div>

      {/* текст */}
      <div>
        <label className="sup-form__label">Подробности</label>
        <textarea
          className="sup-form__textarea"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Опиши что не работает, когда заметил, что пытался сделать. Можно прикрепить скриншот."
          maxLength={4000}
        />
        <div className="sup-form__hint">
          {text.length}/4000
        </div>
      </div>

      {/* вложения */}
      <div>
        <label className="sup-form__label">Вложения (по желанию)</label>
        <TicketAttachmentUpload
          attachments={attachments}
          onChange={setAttachments}
        />
      </div>

      {/* email для гостей */}
      {!isAuthenticated && (
        <GuestEmailField email={guestEmail} onEmailChange={setGuestEmail} />
      )}

      {err && <div className="sup-msg-err">{err}</div>}

      <div>
        <button type="submit" className="sup-btn-primary" disabled={busy}>
          <PaperAirplaneIcon size={16} />
          {busy ? "Отправляем…" : "Отправить обращение"}
        </button>
      </div>
    </form>
  );
}
